import { HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { Observable } from 'rxjs';
import {
  IExportCsvPollResponse,
  IExportCsvStartResponse,
  IPaymentReceiptItemResponse,
  ITransactionHistoriesResponse,
  TransactionType,
} from '../models';
import { DatePipe } from '@angular/common';
import { AdvancedSearchConfigResponse } from '@carm/shared/components/advanced-search/models/advanced-search.model';
import { environment } from 'src/environments/environment';
import { LoaderSpinnerTypes } from '@carm/_core/models/loader-types.model';

@Injectable()
export class TransactionHistoryService extends BaseApiService {
  constructor(protected injector: Injector, private datePipe: DatePipe) {
    super(injector);
  }
  get financialHistory(): string {
    return `${this.endPointBP}/financial/history`;
  }

  get financialHistoryClient(): string {
    return `${this.endPointBP}/financial/history/client`;
  }

  public getFinancialTransactionHistory(
    pageIndex: number,
    pageSize: number,
    minDate: Date,
    maxDate: Date,
    type: TransactionType,
    sortField: string,
    searchString: string = '',
    transactionHistoryPageType: string = 'OWNER'
  ): Observable<ITransactionHistoriesResponse> {
    const endPoint =
      transactionHistoryPageType === 'CLIENTS'
        ? `${this.financialHistoryClient}/list`
        : `${this.financialHistory}/list`;

    const params = new HttpParams()
      .set('currentPage', pageIndex.toString())
      .set('pageSize', pageSize.toString())
      .set('startDate', this.datePipe.transform(minDate, 'yyyy-MM-dd'))
      .set('endDate', this.datePipe.transform(maxDate, 'yyyy-MM-dd'))
      .set('transactionHistorySort', sortField)
      .set('transactionType', type.toString())
      .set('searchString', searchString || '');

    return this.httpAuthGet<ITransactionHistoriesResponse>(
      endPoint,
      params,
      LoaderSpinnerTypes.AgGrid
    );
  }

  public getFinancialTransactionHistoryByBusinessAndProgram(
    bn9: string,
    programId: string,
    pageIndex: number,
    pageSize: number,
    minDate: Date,
    maxDate: Date,
    type: TransactionType,
    sortField: string,
    searchString: string = ''
  ): Observable<ITransactionHistoriesResponse> {
    const endPoint = `${environment.baseApiUrls}/business/${bn9}/program/${programId}/financial/history/list`;

    const params = new HttpParams()
      .set('currentPage', pageIndex.toString())
      .set('pageSize', pageSize.toString())
      .set('startDate', this.datePipe.transform(minDate, 'yyyy-MM-dd'))
      .set('endDate', this.datePipe.transform(maxDate, 'yyyy-MM-dd'))
      .set('transactionHistorySort', sortField)
      .set('transactionType', type.toString())
      .set('searchString', searchString);

    return this.httpAuthGet<ITransactionHistoriesResponse>(
      endPoint,
      params
    );
  }

  public paymentReceipt(
    documentNumber: string,
    subItemNumber: string,
    clientNumber: string,
    transactionHistoryPageType: string = 'OWNER'
  ): Observable<IPaymentReceiptItemResponse> {
    let endpoint =
      transactionHistoryPageType === 'CLIENTS'
        ? `${this.financialHistoryClient}/document-number/${documentNumber}/receipt?subItemNumber=${subItemNumber}&fields=DEFAULT`
        : `${this.financialHistory}/document-number/${documentNumber}/receipt?subItemNumber=${subItemNumber}&fields=DEFAULT`;

    if (clientNumber && clientNumber !== this.carmRouter.bn15) {
      endpoint = this.replaceNativeBnByClientNumber(endpoint, clientNumber);
    }
    return this.httpAuthGet<IPaymentReceiptItemResponse>(endpoint);
  }

  getFinancialTransactionsSearchOptions(
    transactionHistoryPageType: string = 'OWNER'
  ) {
    const params = new HttpParams();
    const endPoint =
      transactionHistoryPageType === 'CLIENTS'
        ? `${this.financialHistoryClient}/financial-transaction/search`
        : `${this.financialHistory}/financial-transaction/search`;
    return this.httpAuthGet<AdvancedSearchConfigResponse>(endPoint, params);
  }

  getStatisticalTransactionsSearchOptions(
    transactionHistoryPageType: string = 'OWNER'
  ) {
    const params = new HttpParams();
    const endPoint =
      transactionHistoryPageType === 'CLIENTS'
        ? `${this.financialHistoryClient}/statistical-transaction/search`
        : `${this.financialHistory}/statistical-transaction/search`;
    return this.httpAuthGet<AdvancedSearchConfigResponse>(endPoint, params);
  }

  // returns the exportId that should be used for polling.
  public exportCsvStart(
    minDate: string,
    maxDate: string,
    type: TransactionType,
    sortField: string,
    searchString: string,
    transactionHistoryPageType: string = 'OWNER'
  ): Observable<IExportCsvStartResponse> {
    const params = new HttpParams()
      .set('startDate', minDate)
      .set('endDate', maxDate)
      .set('transactionHistorySort', sortField)
      .set('transactionType', type.toString())
      .set('searchString', searchString);

    const endpoint =
      transactionHistoryPageType === 'CLIENTS'
        ? `${this.financialHistoryClient}/export?fields=DEFAULT`
        : `${this.financialHistory}/export?fields=DEFAULT`;
    return this.httpAuthGet<IExportCsvStartResponse>(endpoint, params);
  }

  public exportCsvPoll(
    exportId: string,
    transactionHistoryPageType: string = 'OWNER'
  ): Observable<IExportCsvPollResponse> {
    const endpoint =
      transactionHistoryPageType === 'CLIENTS'
        ? `${this.financialHistoryClient}/export/${exportId}/poll`
        : `${this.financialHistory}/export/${exportId}/poll`;
    return this.httpAuthGet<IExportCsvPollResponse>(endpoint);
  }

  public exportCsvDownload(
    exportId: string,
    transactionHistoryPageType: string = 'OWNER'
  ): Observable<Blob> {
    const endpoint =
      transactionHistoryPageType === 'CLIENTS'
        ? `${this.financialHistoryClient}/export/${exportId}/download`
        : `${this.financialHistory}/export/${exportId}/download`;
    return this.httpAuthDownloadFile(endpoint);
  }

  // Replaces the endpoint BN by the client's BN to allow broker to view receipt
  private replaceNativeBnByClientNumber(endPoint: string, clientNumber: string): string {
    const newEndPoint = endPoint.replace(this.carmRouter.bn15, clientNumber).replace(this.carmRouter.bn9, clientNumber.substr(0,9));
    return newEndPoint;
  }
}
